/**
 * LvHomeSurface — the vendored Console Home renderer, adapted to the LV shell.
 *
 * The heavy lifting (hero, stat/tile grids, callouts, inner pages with a Back
 * trail) is the vendored HomeSurface — this file is the LV seam around it:
 *
 *   TRANSPORT. The loader rides apiFetch (the shell session bearer, sliding
 *   renewal, 401-redirect-to-gate) against /LearnerView/api/home, where the
 *   console used its own session helpers. A 204 means "no content registered"
 *   and renders the vendored empty state, same as the console.
 *
 *   CHROME. bare mode: the LV tab strip is already the bar — HomeSurface's own
 *   header would be a second one. Back navigation and the site nav flow inside
 *   the content instead (the vendored component's bare layout).
 *
 *   SURFACE TILES. A tile action written as `surface:<id>` in a content repo
 *   (e.g. `[Open your mailbox](surface:inbox)`) is promoted to a real surface
 *   tile, so the renderer's "Open →" path calls onOpenSurface and the shell
 *   opens that surface as a tab. When the client's allowlist does NOT offer
 *   the surface, the tile renders as plain content without the affordance —
 *   mirroring the console, which never offers an unopenable tile. `surface:`
 *   hrefs anywhere else (hero actions, nav, footer) are stripped: the
 *   vendored follow() would hand them to window.open.
 */
import { useCallback, useMemo } from 'react'
import HomeSurface, { type HomeLoader } from './vendor/home/HomeSurface'
import type { HomeDoc, HomeNavLink } from './vendor/home/types'
import { apiFetch } from '../shell/auth'

const SURFACE_HREF_RE = /^surface:([a-z0-9-]+)$/i

async function fetchHomeDoc(file?: string): Promise<HomeDoc | null> {
  const url = file
    ? `/LearnerView/api/home/page?file=${encodeURIComponent(file)}`
    : '/LearnerView/api/home'
  const r = await apiFetch(url)
  if (r.status === 204) return null
  if (!r.ok) throw new Error(`home unavailable (${r.status})`)
  return (await r.json()) as HomeDoc
}

function surfaceIdOf(href: string): string | undefined {
  return SURFACE_HREF_RE.exec(href.trim())?.[1]
}

/** Rewrite a fetched doc for THIS deployment: surface: tile hrefs become real
 *  surface tiles when offered, inert content when not. */
function adaptDoc(doc: HomeDoc, offered: ReadonlySet<string>): HomeDoc {
  const sections = doc.sections.map((s) => ({
    ...s,
    tiles: s.tiles.map((t) => {
      const id = t.action ? surfaceIdOf(t.action.href) : undefined
      if (!id) return t
      const { action: _, ...rest } = t
      return offered.has(id) ? { ...rest, surface: id } : rest
    }),
    actions: s.actions.filter((a) => !surfaceIdOf(a.href)),
  }))
  return {
    ...doc,
    sections,
    hero: { ...doc.hero, actions: doc.hero.actions.filter((a) => !surfaceIdOf(a.href)) },
    nav: doc.nav.filter((n) => !surfaceIdOf(n.href)),
    footer: doc.footer.filter((f) => !surfaceIdOf(f.href)),
  }
}

export default function LvHomeSurface({
  offeredSurfaces,
  onOpenSurface,
  onNavChange,
  navFollowRef,
}: {
  /** Surface ids this deployment offers (the resolved allowlist). */
  offeredSurfaces: string[]
  /** Open a surface as a shell tab — the same open() the launcher uses. */
  onOpenSurface: (id: string, label?: string) => void
  /** The masthead's Explore menu mirrors Home's own nav: the vendored
   *  HomeSurface reports its effective menu + current page here, and
   *  navFollowRef hands its follow() to the masthead so an Explore pick
   *  navigates Home exactly the way its own nav row does. */
  onNavChange?: (items: HomeNavLink[], currentFile: string | undefined) => void
  navFollowRef?: { current: ((n: HomeNavLink) => void) | null }
}) {
  const offered = useMemo(() => new Set(offeredSurfaces), [offeredSurfaces])
  const load: HomeLoader = useCallback(
    async (file?: string) => {
      const doc = await fetchHomeDoc(file)
      return doc ? adaptDoc(doc, offered) : null
    },
    [offered],
  )
  return (
    <HomeSurface
      load={load} bare onOpenSurface={onOpenSurface}
      onNavChange={onNavChange} navFollowRef={navFollowRef}
    />
  )
}
